/* oxlint-disable max-lines */
import { EClustersType } from 'shared/map-search/types/clustersType';

import {
  POINT_RADIUS,
  DOT_SIZE_SMALL,
  DOT_SIZE_BIG,
  POINT_COLORS,
  REDESIGNED_POINT_RADIUS,
  POINT_BORDER_WIDTH,
  POINT_WITH_COUNT_RADIUS,
  POINT_WITH_COUNT_BORDER_WIDTH,
  POINT_COUNT_MAX_VALUE,
  POINT_COUNT_MIN_VALUE,
  POINT_GLOW_RADIUS,
  EXTENDED_POINT_BORDER_WIDTH,
  EXTENDED_POINT_RADIUS,
  POINT_PEAS_RADIUS,
  DOT_SIZE_BIG_REDESIGN,
  DOT_SIZE_SMALL_REDESIGN,
} from '../../../constants/map';
import { TFeature } from '../../../types/map';

type TPointColors = Pick<
  IPointConfig,
  'color' | 'borderColor' | 'textColor' | 'hoverColor' | 'hoverOutlineColor' | 'hoverBorderColor' | 'hoverTextColor'
>;

export enum FPointType {
  Regular = 1,
  ResidentialComplex = 2,
  Extended = 4,
  Viewed = 8,
  ResidentialComplexPeas = 16,
  ExtendedResidentialComplex = 32,
}

export interface IPointConfig {
  radius: number;
  color: string;
  textColor?: string;
  borderColor?: string;
  borderWidth?: number;
  count?: string;
  hasContourShadow: boolean;
  glowRadius?: number;
  glowPulsed?: boolean;
  glowPulsedColor: string;
  hideShadow?: boolean;
  hoverColor?: string;
  hoverTextColor?: string;
  hoverBorderColor?: string;
  hoverOutlineColor?: string;
}

export interface IGetPointConfigParameters {
  feature: TFeature;
  isResidentialComplexesListing: boolean;
  isResultable: boolean;
  isDetailed: boolean;
  isRedesign: boolean;
  // @todo выпилить эксп CD-159979
  showOnlyPrice: boolean;
  pointType: EClustersType | null;
  isMobileView: boolean;
}

const COLORS_CONFIG_MAP: { [key: number]: TPointColors } = {
  [FPointType.Regular]: { color: '#0468ff', borderColor: '#fff' },
  [FPointType.Regular | FPointType.Viewed]: { color: '#83b4ff', borderColor: '#fff' },
  [FPointType.ResidentialComplex]: { color: '#3ac500', borderColor: '#fff' },
  [FPointType.ResidentialComplex | FPointType.Viewed]: { color: '#B0E899', borderColor: '#fff' },
  [FPointType.ResidentialComplexPeas]: { color: '#34AC0A', borderColor: '#fff' },
  [FPointType.ResidentialComplexPeas | FPointType.Viewed]: { color: '#B0E899', borderColor: '#fff' },
  [FPointType.Extended]: { color: '#fff', borderColor: '#0468ff', textColor: '#152242' },
  [FPointType.Extended | FPointType.Viewed]: { color: '#fff', borderColor: '#83b4ff', textColor: '#152242' },
  [FPointType.ExtendedResidentialComplex]: { color: '#fff', borderColor: '#3ac500', textColor: '#152242' },
  [FPointType.ExtendedResidentialComplex | FPointType.Viewed]: {
    color: '#fff',
    borderColor: '#3ac500',
    textColor: '#152242',
  },
};

const COLORS_CONFIG_MAP_V2: { [key: number]: TPointColors } = {
  [FPointType.Regular]: {
    color: '#005EDE',
    borderColor: '#fff',
    hoverColor: '#005EDE',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(0, 94, 222, 0.20)',
  },
  [FPointType.Regular | FPointType.Viewed]: {
    color: '#CDE1FF',
    borderColor: '#fff',
    hoverColor: '#005EDE',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(0, 94, 222, 0.20)',
  },
  [FPointType.ResidentialComplex]: {
    color: '#227E01',
    borderColor: '#fff',
    hoverColor: '#227E01',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(41, 154, 1, 0.40)',
  },
  [FPointType.ResidentialComplex | FPointType.Viewed]: {
    color: '#D8F3CC',
    borderColor: '#fff',
    hoverColor: '#227E01',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(41, 154, 1, 0.40)',
  },
  [FPointType.ResidentialComplexPeas]: {
    color: '#227E01',
    borderColor: '#fff',
    hoverColor: '#227E01',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(41, 154, 1, 0.40)',
  },
  [FPointType.ResidentialComplexPeas | FPointType.Viewed]: {
    color: '#D8F3CC',
    borderColor: '#fff',
    hoverColor: '#227E01',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(41, 154, 1, 0.40)',
  },
  [FPointType.Extended]: {
    color: '#fff',
    borderColor: '#005EDE',
    textColor: '#005EDE',
    hoverColor: '#005EDE',
    hoverBorderColor: '#fff',
    hoverTextColor: '#fff',
    hoverOutlineColor: 'rgba(0, 94, 222, 0.20)',
  },
  [FPointType.Extended | FPointType.Viewed]: {
    color: '#fff',
    borderColor: '#CDE1FF',
    textColor: '#CDE1FF',
    hoverColor: '#005EDE',
    hoverTextColor: '#fff',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(0, 94, 222, 0.20)',
  },
  [FPointType.ExtendedResidentialComplex]: {
    color: '#fff',
    borderColor: '#227E01',
    textColor: '#227E01',
    hoverColor: '#227E01',
    hoverTextColor: '#fff',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(41, 154, 1, 0.40)',
  },
  [FPointType.ExtendedResidentialComplex | FPointType.Viewed]: {
    color: '#fff',
    borderColor: '#D8F3CC',
    textColor: '#D8F3CC',
    hoverColor: '#227E01',
    hoverTextColor: '#fff',
    hoverBorderColor: '#fff',
    hoverOutlineColor: 'rgba(41, 154, 1, 0.40)',
  },
};

// @todo выпилить эксп CD-159979
const POSUTOCHNO_EXP_POINT_CONFIG = {
  radius: 1,
  color: 'rgba(0,0,0,0)',
  textColor: 'rgba(0,0,0,0)',
  borderColor: 'rgba(0,0,0,0)',
  borderWidth: 1,
  count: '0',
  hasContourShadow: false,
  glowPulsedColor: '0, 0, 0',
};

type TPointSize = Pick<IPointConfig, 'radius' | 'borderWidth'>;

function getPointType(feature: TFeature, isResidentialComplexesListing: boolean, isDetailed: boolean): FPointType {
  if (feature.properties.type === 'peas') {
    return FPointType.ResidentialComplexPeas;
  }

  if (feature.properties.isNewbuildingMainPin) {
    return feature.properties.isExtended && !feature.properties.isActive
      ? FPointType.ExtendedResidentialComplex
      : FPointType.ResidentialComplex;
  }

  if (feature.properties.hasNewbuildingsOnly && feature.properties.isExtended && !feature.properties.isActive) {
    return FPointType.ExtendedResidentialComplex;
  }

  if (feature.properties.hasNewbuildingsOnly && !isDetailed) {
    return FPointType.ResidentialComplex;
  }

  if (feature.properties.isExtended && !feature.properties.isActive) {
    return FPointType.Extended;
  }

  if (
    ((isResidentialComplexesListing || isDetailed || feature.properties.newbuilding?.isAnyFicheringPlus) &&
      feature.properties.hasNewobject) ||
    feature.properties.isAnyFromKp
  ) {
    return FPointType.ResidentialComplex;
  }

  return FPointType.Regular;
}

export function getPointConfig({
  feature,
  isResidentialComplexesListing,
  isResultable,
  isDetailed,
  // @todo выпилить эксп CD-159979
  showOnlyPrice,
  pointType: type,
  isMobileView,
  isRedesign,
}: IGetPointConfigParameters): IPointConfig {
  if (type) {
    return applyPointRedesign(getPointConfigByType({ feature, type, isRedesign }), feature, isRedesign);
  }

  // @todo выпилить эксп CD-159979
  if (showOnlyPrice) {
    return POSUTOCHNO_EXP_POINT_CONFIG;
  }

  const pointType = getPointType(feature, isResidentialComplexesListing, isDetailed);

  const colorsMap = isRedesign ? COLORS_CONFIG_MAP_V2 : COLORS_CONFIG_MAP;

  const colors =
    colorsMap[pointType | (feature.properties.isViewed && !feature.properties.isActive ? FPointType.Viewed : 0)];
  const pointColors = getHoveredPointColors(colors, feature);

  const glowPulsed = feature.properties.isPulsedGlow && !feature.properties.isActive && !feature.properties.isViewed;

  const glowPulsedColor =
    pointType === FPointType.ResidentialComplex || pointType === FPointType.ResidentialComplexPeas
      ? '56, 188, 1'
      : '4, 104, 255';
  const { radius, borderWidth } = getPointSize(feature, isResultable, isDetailed, pointType, isRedesign);

  let glowRadius;
  if (
    feature.properties.isGlow &&
    (!feature.properties.favoriteIds || feature.properties.favoriteIds.length === 0) &&
    // @todo разработка в MNZ-700
    !feature.properties.isPulsedGlow
  ) {
    glowRadius = feature.properties.isActive ? 8 : POINT_RADIUS + 4;
  }

  if (pointType === FPointType.ResidentialComplexPeas && feature.properties.isActive) {
    glowRadius = 10;
  }

  let count: string | undefined;
  if (isResultable && !isDetailed) {
    if (feature.properties.count > 1 && feature.properties.count <= 9) {
      count = String(feature.properties.count);
    } else if (feature.properties.count > 9) {
      count = '9+';
    }
  }

  const hideShadow = isRedesign || Boolean(feature.properties.newbuilding?.isAnyFicheringPlus && isMobileView);

  return applyPointRedesign(
    {
      ...pointColors,
      glowPulsed,
      glowPulsedColor,
      glowRadius,
      borderWidth,
      radius,
      count,
      hasContourShadow: !isRedesign && isResultable && !isDetailed,
      hideShadow,
    },
    feature,
    isRedesign,
  );
}

function applyPointRedesign(config: IPointConfig, feature: TFeature, isRedesign: boolean): IPointConfig {
  if (!isRedesign) {
    return config;
  }

  if (feature.properties.isHovered && !feature.properties.isBalloonVisible) {
    return {
      ...config,
      borderWidth: 1,
      glowPulsed: false,
    };
  }

  if (feature.properties.isHovered || feature.properties.isActive) {
    return {
      ...config,
      radius: 4,
      borderWidth: 0,
      glowPulsed: false,
      hoverOutlineColor: undefined,
    };
  }

  if (!feature.properties.isPulsedGlow) {
    return config;
  }

  if (!config.glowPulsed) {
    return {
      ...config,
      borderWidth: 1,
    };
  }

  return config;
}

function getHoveredPointColors(colors: TPointColors, feature: TFeature): TPointColors {
  const { hoverColor, hoverOutlineColor, hoverBorderColor, hoverTextColor, ...pointColors } = colors;

  if (!feature.properties.isHovered || !hoverColor) {
    return pointColors;
  }

  return {
    ...pointColors,
    color: hoverColor,
    textColor: hoverTextColor,
    borderColor: hoverBorderColor,
    hoverColor,
    hoverTextColor,
    hoverBorderColor,
    hoverOutlineColor,
  };
}

function getPointSize(
  feature: TFeature,
  isResultable: boolean,
  isDetailed: boolean,
  pointType: FPointType,
  isRedesign: boolean,
): TPointSize {
  if (pointType === FPointType.ResidentialComplexPeas) {
    return {
      radius: POINT_PEAS_RADIUS,
      borderWidth: getPeasPointBorderWidth(isRedesign),
    };
  }

  return {
    radius: getDefaultPointRadius(feature, isResultable, isDetailed, isRedesign),
    borderWidth: getDefaultPointBorderWidth(feature, isResultable, isDetailed, isRedesign),
  };
}

function getPeasPointBorderWidth(isRedesign: boolean): number {
  return getRedesignPointBorderWidth(isRedesign) ?? 1;
}

function getRedesignPointBorderWidth(isRedesign: boolean): number | undefined {
  if (!isRedesign) {
    return;
  }

  return 1;
}

function getDefaultPointRadius(
  feature: TFeature,
  isResultable: boolean,
  isDetailed: boolean,
  isRedesign: boolean,
): number {
  const { count, isActive, isExtended } = feature.properties;
  const isResultPoint = isResultable && !isDetailed;

  if (isRedesign) {
    if (isResultPoint && count > 1) {
      return DOT_SIZE_BIG_REDESIGN;
    }

    return DOT_SIZE_SMALL_REDESIGN;
  }

  if (isActive) {
    return 3;
  }

  if (isResultPoint && count === 1) {
    return DOT_SIZE_SMALL;
  }

  if (isResultPoint && count > 1) {
    return DOT_SIZE_BIG;
  }

  if (!isResultable && isExtended) {
    return POINT_RADIUS - 1;
  }

  return POINT_RADIUS;
}

function getDefaultPointBorderWidth(
  feature: TFeature,
  isResultable: boolean,
  isDetailed: boolean,
  isRedesign: boolean,
): number {
  const { isActive, isExtended, isGlow } = feature.properties;

  const redesignBorderWidth = getRedesignPointBorderWidth(isRedesign);

  if (redesignBorderWidth !== undefined) {
    return redesignBorderWidth;
  }

  if (isResultable && !isDetailed) {
    return 2;
  }

  if (!isDetailed) {
    return 1;
  }

  if (isActive) {
    return 0;
  }

  if (isExtended) {
    return 2;
  }

  if (isGlow) {
    return 1;
  }

  return 0;
}

interface IGetPointConfigByTypeParameters {
  feature: TFeature;
  type: EClustersType;
  isRedesign: boolean;
}

function getPointConfigByType(params: IGetPointConfigByTypeParameters): IPointConfig {
  const colorSettings = params.isRedesign ? getPointConfigByTypeRedesignColors(params) : getPointColors(params);
  const radiusSettings = getPointRadius(params);
  const count = getPointCount(params);
  const glowRadius = getPointGlowRadius(params);
  const glowPulsed = getPointGlowPulsed(params);

  return {
    ...colorSettings,
    ...radiusSettings,
    count,
    glowRadius,
    glowPulsed,
    hasContourShadow: !params.isRedesign,
    hideShadow: params.isRedesign,
  };
}

function getPointConfigByTypeRedesignColors(params: IGetPointConfigByTypeParameters): TPointColors & IGetPointColors {
  const colors = getPointColors(params);
  const redesignColors = getRedesignPointColors(params.feature);

  return {
    ...colors,
    ...getHoveredPointColors(
      {
        ...redesignColors,
        color: colors.color,
        borderColor: colors.borderColor,
        textColor: colors.textColor,
      },
      params.feature,
    ),
  };
}

function getRedesignPointColors(feature: TFeature): TPointColors {
  const pointType =
    feature.properties.isAnyFromKp ||
    feature.properties.newbuilding?.isAnyFicheringPlus ||
    feature.properties.hasNewobject
      ? FPointType.ResidentialComplexPeas
      : FPointType.Regular;

  return COLORS_CONFIG_MAP_V2[
    pointType | (feature.properties.isViewed && !feature.properties.isActive ? FPointType.Viewed : 0)
  ];
}

interface IGetPointColors {
  color: string;
  borderColor: string;
  textColor?: string;
  glowPulsedColor: string;
}

/**
 * @description
 * Получаем цвета пина в зависимости от свойств пина
 */
function getPointColors({ feature }: IGetPointConfigByTypeParameters): IGetPointColors {
  const { isActive, isExtended, isAnyFromKp, newbuilding, hasNewobject, isHovered } = feature.properties;

  const {
    white,
    lightBlue,
    regularBlue,
    hoveredBlue,
    activeBlue,
    lightGreen,
    regularGreen,
    hoveredGreen,
    activeGreen,
    textBlue,
    textGreen,
  } = POINT_COLORS;

  const isViewed = feature.properties.isViewed && !isActive;

  /**
   * @description Пины объявлений из новостроек и коттеджных поселков
   */
  if (isAnyFromKp || newbuilding?.isAnyFicheringPlus || hasNewobject) {
    /**
     * @description Пины рекомендованных объявлений
     */
    if (isExtended && !isActive) {
      return {
        color: white,
        borderColor: isViewed ? lightGreen : regularGreen,
        textColor: textGreen,
        glowPulsedColor: '0, 0, 0',
      };
    }

    let color = regularGreen;

    if (isViewed && !isActive) {
      color = lightGreen;
    } else if (isActive) {
      color = activeGreen;
    } else if (isHovered) {
      color = hoveredGreen;
    }

    return {
      color,
      borderColor: white,
      glowPulsedColor: '56, 188, 1',
    };
  }

  /**
   * @description Пины рекомендованных объявлений
   */
  if (isExtended && !isActive) {
    return {
      color: white,
      borderColor: isViewed ? lightBlue : regularBlue,
      textColor: textBlue,
      glowPulsedColor: '0, 0, 0',
    };
  }

  let color = regularBlue;

  if (isViewed && !isActive) {
    color = lightBlue;
  } else if (isActive) {
    color = activeBlue;
  } else if (isHovered) {
    color = hoveredBlue;
  }

  /** @description Пины остальных объявлений */
  return {
    color,
    borderColor: white,
    glowPulsedColor: '4, 104, 255',
  };
}

interface IGetPointRadius {
  radius: number;
  borderWidth: number;
}

function getPointRadius({ feature, type }: IGetPointConfigByTypeParameters): IGetPointRadius {
  const { count, isExtended } = feature.properties;

  if (count === 1 || type === EClustersType.Peas || type === EClustersType.Detailed) {
    return isExtended
      ? {
          radius: EXTENDED_POINT_RADIUS,
          borderWidth: EXTENDED_POINT_BORDER_WIDTH,
        }
      : {
          radius: REDESIGNED_POINT_RADIUS,
          borderWidth: POINT_BORDER_WIDTH,
        };
  }

  return {
    radius: POINT_WITH_COUNT_RADIUS,
    borderWidth: POINT_WITH_COUNT_BORDER_WIDTH,
  };
}

function getPointCount({ feature, type }: IGetPointConfigByTypeParameters): string | undefined {
  const { count } = feature.properties;

  if (type === EClustersType.Detailed || type === EClustersType.Peas || count < POINT_COUNT_MIN_VALUE) {
    return;
  }

  if (count > POINT_COUNT_MAX_VALUE) {
    return `${POINT_COUNT_MAX_VALUE}+`;
  }

  return String(count);
}

function getPointGlowRadius({ feature }: IGetPointConfigByTypeParameters): number | undefined {
  const { isGlow, isPulsedGlow, favoriteIds } = feature.properties;

  const hasFavorites = !!favoriteIds && favoriteIds.length > 0;

  return isGlow && !hasFavorites && !isPulsedGlow ? POINT_GLOW_RADIUS : undefined;
}

function getPointGlowPulsed({ feature }: IGetPointConfigByTypeParameters): boolean | undefined {
  const { isPulsedGlow, isActive, isViewed } = feature.properties;

  return isPulsedGlow && !isActive && !isViewed;
}
